import {
  AuthProvider,
  CustomLoginForm,
  createCurrentUserProvisionValidator,
  useAuth,
} from "@assetwatch/auth";
import {
  Alert,
  AssetWatchUiProvider,
  Container,
  Stack,
  Title,
} from "@assetwatch/ui";
import * as Sentry from "@sentry/react";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { AppFeatureGate } from "../components/AppFeatureGate";
import { AppNotFound } from "../components/AppNotFound";
import LaunchDarklyProvider from "../components/LaunchDarkly/LaunchDarklyProvider";
import config from "../config";
import { MlFrontendApiError } from "../errors/errors";
import { fetchMlFrontendUserData } from "../lib/currentUserData";

const authApiBaseUrl =
  (import.meta.env.VITE_AUTH_API_BASE_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "";
const hasProvisioningApi = authApiBaseUrl.length > 0;

const validateAuthenticatedUser = hasProvisioningApi
  ? createCurrentUserProvisionValidator({})
  : undefined;
const signOutRedirect = {
  clientId: config.cognito.APP_CLIENT_ID,
  domain: config.cognito.DOMAIN,
  redirectUri: `${window.location.origin}/`,
};

async function lookupLoginMethod(email: string) {
  if (!hasProvisioningApi) {
    return { authType: "basic" as const };
  }

  const response = await fetch(`${authApiBaseUrl}/user/usertype`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ user_email: email }),
  });

  if (!response.ok) {
    throw new Error(`Auth lookup failed with ${response.status}`);
  }

  const data = (await response.json()) as {
    authorize_href?: string;
    identityProvider?: string;
  };

  if (data.authorize_href && data.identityProvider) {
    return {
      authType: "sso" as const,
      identityProvider: data.identityProvider,
    };
  }

  return { authType: "basic" as const };
}

function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, refreshAuth, validationError } =
    useAuth();
  const href = useRouterState({
    select: (state) => state.location.pathname + state.location.searchStr,
  });
  const isPasswordResetRoute = href.startsWith("/login/reset");

  if (isPasswordResetRoute) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <Container size="sm">
        <Stack gap={16}>
          <Title size="lg">Loading ML Frontend</Title>
          <Alert tone="informative" title="Checking your session..." />
        </Stack>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <CustomLoginForm
        authenticate={refreshAuth}
        forgotPasswordHref={(email) =>
          `/login/reset?email=${encodeURIComponent(email)}`
        }
        initialStatusMessage={validationError ?? undefined}
        isAuthenticated={isAuthenticated}
        lookupLoginMethod={lookupLoginMethod}
        onNavigate={() => {
          window.location.replace(href);
        }}
        redirectTo={href}
      />
    );
  }

  return <>{children}</>;
}

function SentryUserScopeSync() {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.sub, email: user.email });
      return;
    }

    Sentry.setUser(null);
  }, [user]);

  return null;
}

function RootProviders() {
  return (
    <QueryClientProvider client={Route.useRouteContext().queryClient}>
      <AuthProvider
        fetchUserData={hasProvisioningApi ? fetchMlFrontendUserData : undefined}
        signOutRedirect={signOutRedirect}
        validateAuthenticatedUser={validateAuthenticatedUser}
      >
        <LaunchDarklyProvider>
          <SentryUserScopeSync />
          <AssetWatchUiProvider
            defaultTheme="light"
            storageKey="assetwatch-ml-frontend-theme"
          >
            <AuthGate>
              <AppFeatureGate>
                <Outlet />
              </AppFeatureGate>
            </AuthGate>
          </AssetWatchUiProvider>
        </LaunchDarklyProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RootError({ error }: { error: Error }) {
  if (error instanceof MlFrontendApiError && error.status >= 500) {
    return (
      <Container size="sm">
        <Stack gap={16}>
          <Title size="lg">Unable to load ML Frontend</Title>
          <Alert
            tone="negative"
            title="Data temporarily unavailable"
            subtext="ML Frontend data is temporarily unavailable. Refresh and try again."
          />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="sm">
      <Stack gap={16}>
        <Title size="lg">Unable to load ML Frontend</Title>
        <Alert tone="negative" title={error.message} />
      </Stack>
    </Container>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootProviders,
  errorComponent: RootError,
  notFoundComponent: AppNotFound,
  pendingComponent: () => (
    <Container size="sm">
      <Stack gap={16}>
        <Title size="lg">Loading ML Frontend</Title>
        <Alert tone="informative" title="Loading app view..." />
      </Stack>
    </Container>
  ),
});
